import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';

import { Button } from '@app/shared/ui/atoms/Common';
import { cn } from '@app/shared/utils/common';

export const statusButtonVariants = cva(
  'flex justify-center items-center w-fit h-auto p-2.5 border rounded-full text-xs font-semibold whitespace-nowrap',
  {
    variants: {
      variant: {
        pending: 'border-[#f4c430] bg-[#f4c4301a] text-[#f4c430]',
        reviewing: 'border-[#4da1ff] bg-[#4da1ff1a] text-[#4da1ff]',
        confirmed: 'border-[#3cb371] bg-[#3cb3711a] text-[#3cb371]',
        rejected: 'border-[#e74c3c] bg-[#e74c3c1a] text-[#e74c3c]',
        canceled: 'border-[#95a5a6] bg-[#95a5a61a] text-[#95a5a6]',
      },
      active: {
        true: 'text-foreground',
      },
    },
    compoundVariants: [
      { variant: 'pending', active: true, class: 'bg-[#f4c430]' },
      { variant: 'reviewing', active: true, class: 'bg-[#4da1ff]' },
      { variant: 'confirmed', active: true, class: 'bg-[#3cb371]' },
      { variant: 'rejected', active: true, class: 'bg-[#e74c3c]' },
      { variant: 'canceled', active: true, class: 'bg-[#95a5a6]' },
    ],
  }
);

export type StatusButtonVariant = Exclude<
  VariantProps<typeof statusButtonVariants>['variant'],
  null | undefined
>;

interface StatusButtonProps
  extends
    React.HTMLAttributes<HTMLElement>,
    Omit<VariantProps<typeof statusButtonVariants>, 'variant'> {
  variant: StatusButtonVariant;
  as?: 'button' | 'span';
}

export function StatusButton({
  variant,
  active,
  className,
  as = 'button',
  ...rest
}: StatusButtonProps) {
  const { t } = useTranslation();

  const props = {
    className: cn(statusButtonVariants({ variant, active }), className),
    children: t(`withdrawal.statuses.${variant}`),
    ...rest,
  };

  return as === 'button' ? <Button variant="custom" {...props} /> : <span {...props} />;
}
